import { useNotificationServiceNotificationItemsList } from '@notification/api/queries'
import { ProcessedNotificationList } from '@notification/api/requests'
import { renderTemplateAsText, TemplateContext } from '@notification/utils'
import { ConditionalText, DateTimeCell } from '@shared/components'
import { usePaginationConfig, useQueryUpdate, useSorting } from '@shared/hooks'
import { Flex, Table, Tag, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'
import { NotificationTemplatePreview } from '../NotificationTemplatePreview'

const colorStatuses = {
  cancelled: 'red',
  distributed: 'green',
  failed: 'red',
  queued: 'blue',
  waiting: 'gray'
}

type Props = {
  onUpdate?: React.Dispatch<
    React.SetStateAction<{ count: number; isLoading: boolean }>
  >
  onClick?: (id: number) => void
}

/**
 * The list endpoint returns the context the message was rendered with, but it
 * is missing from the generated model.
 */
type NotificationRow = ProcessedNotificationList & {
  context?: TemplateContext
}

export const NotificationTable = ({ onUpdate, onClick }: Props) => {
  const { t } = useTranslation()
  const { params, updateQuery } = useQueryUpdate()
  const { styles } = useStyles()

  const { triggerSort, orderQueryParams, getSortedTableColumns } =
    useSorting<NotificationRow>()

  const { data, isLoading } = useNotificationServiceNotificationItemsList({
    ...params,
    ...orderQueryParams
  })

  useEffect(() => {
    onUpdate?.({ count: data?.count || 0, isLoading })
  }, [data?.count, isLoading, onUpdate])

  const columns: ColumnsType<NotificationRow> = getSortedTableColumns([
    {
      title: t('table.subject'),
      dataIndex: 'message_subject',
      key: 'message_subject',
      sorter: true,
      render: (value, { context }) => (
        <ConditionalText condition={!!value} style={{ fontWeight: 'inherit' }}>
          {renderTemplateAsText(value, context)}
        </ConditionalText>
      )
    },
    {
      title: t('table.recipient'),
      dataIndex: 'recipient_data',
      key: 'recipient_data',
      render: (value) => (
        <ConditionalText condition={!!value?.name || !!value?.email}>
          {value?.name || value?.email}
        </ConditionalText>
      )
    },
    {
      title: t('delivery_method'),
      dataIndex: 'delivery_method',
      key: 'delivery_method',
      sorter: true,
      render: (value) => t(`delivery_methods.${value}`)
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (value) => (
        <Tag color={colorStatuses[value as keyof typeof colorStatuses]}>
          {t(`statuses.${value}`)}
        </Tag>
      )
    },
    {
      title: t('table.details'),
      dataIndex: 'message_body',
      key: 'message_body',
      render: (value, { context }) => (
        <ConditionalText condition={!!value} style={{ fontWeight: 'inherit' }}>
          <Tooltip title={renderTemplateAsText(value, context)}>
            <Flex gap={8}>
              <NotificationTemplatePreview value={value} context={context} />
              <div
                className={styles.tableDescription}
                dangerouslySetInnerHTML={{
                  __html: renderTemplateAsText(value, context)
                }}
              />
            </Flex>
          </Tooltip>
        </ConditionalText>
      )
    },
    {
      title: t('table.distribution_date'),
      dataIndex: 'distribution_date',
      width: 170,
      key: 'distribution_date',
      sorter: true,
      render: (date) => <DateTimeCell date={date} />
    }
  ])

  return (
    <Table
      columns={columns}
      dataSource={data?.results}
      loading={isLoading}
      onChange={(_, __, sorter) =>
        triggerSort(Array.isArray(sorter) ? sorter[0] : sorter)
      }
      rowKey="id"
      rowClassName={(record) => {
        const baseClass = 'pointer'
        const readClass = record.is_read ? styles.readRow : styles.unreadRow

        return `${baseClass} ${readClass}`
      }}
      scroll={{ x: 'max-content' }}
      onRow={({ id }) => ({ onClick: () => onClick?.(id) })}
      pagination={{
        ...usePaginationConfig(data),
        onChange: (page: number, pageSize: number) =>
          updateQuery({
            ...params,
            page: page.toString(),
            pageSize: pageSize.toString()
          })
      }}
    />
  )
}
